<template>
  <div class="person-title">
    <template v-if="emoji">
      <span class="emoji">{{ emoji }}</span>
    </template>
    <template v-else>
      <PersonIcon />
    </template>
    <span class="name">{{ title || "Loading..." }}</span>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, shallowRef, toRefs, watch } from "vue";

import { People, ReleasableRef } from "@ctrl/controller-rpc";

import PersonIcon from "@/components/icons/PersonIcon.vue";
import * as inject from "@/components/inject";
import { useEmoji } from "@/composables/useEmoji";
import { useReleaser } from "@/ref";

export default defineComponent({
  name: "PersonTitle",
  components: {
    PersonIcon,
  },
  props: {
    personId: {
      type: String,
      required: true,
    },
  },
  async setup(props) {
    const { personId } = toRefs(props);
    const controller = inject.controller();
    const { title, emoji, updateText } = useEmoji();

    const personRef = shallowRef<ReleasableRef<People | null> | null>(null);
    // Computed property to access the actual task value
    const person = computed<People | null>(
      () => personRef.value?.value ?? null
    );
    watch(
      person,
      (personVal) => {
        if (!personVal) return;
        updateText(personVal.name?.display || personVal.email || "Untitled");
      },
      { immediate: true }
    );

    const releaser = useReleaser();
    personRef.value = await releaser.add(controller.people.get(personId.value));

    return {
      title,
      emoji,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/emoji.scss";

.person-title {
  display: flex;
  align-items: center;
  gap: 0px;
  width: 100%;
}

.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.selected {
    @include themed() {
      color: t($tertiary-50);
    }
  }
}
</style>
